import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckCircleFilled } from "@ant-design/icons";
import { Alert, Card, Checkbox, Col, Collapse, Form, Input, Popconfirm, Radio, Row, Select, Space, Tooltip } from "antd";
import { useMemo } from "react";
import { joinWithAndSeparator } from "@devowl-wp/react-cookie-banner";
import { jsxJoin, parents } from "@devowl-wp/react-utils";
import { useCreateCookiePolicyPageButton } from "./general.js";
import { useFormSettings } from "../../../contexts/formSettings.js";
import { useI18n } from "../../../contexts/i18n.js";
import { useUpsell } from "../../../contexts/upsell.js";
import { useTerritorialLegalBasisArticles } from "../../../hooks/useTerritorialLegalBasisArticles.js";
import { LearnMoreTag } from "../../common/learnMoreTag.js";
import { UpsellTag } from "../../upsell/tag.js";
const { Panel } = Collapse;
const FormSettingsConsentTabContent = ()=>{
    const { __, _i, _n } = useI18n();
    const { isPro } = useUpsell();
    const { isTcf, consentsDeletedAt, iso3166OneAlpha2, ageNotice, ageNoticeCountryAgeMap, predefinedDataProcessingInSafeCountriesLists, onCustomizeClick, bannerlessConsentChecks, renderPageSelector: RenderPageSelector } = useFormSettings();
    const calculateTerritorialLegalBasisArticles = useTerritorialLegalBasisArticles({
        predefinedDataProcessingInSafeCountriesLists,
        iso3166OneAlpha2
    });
    const handleCreateCookiePolicy = useCreateCookiePolicyPageButton();
    const servicesWithoutVisualContentBlocker = useMemo(()=>bannerlessConsentChecks["legalBasisConsentWithoutVisualContentBlocker"].map(({ name })=>name), [
        bannerlessConsentChecks
    ]);
    const showAgeNoticeAgeLimitDropdown = ageNotice ? ageNotice.indexOf("{{minAge}}") > -1 : true;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: (prevValues, curValues)=>JSON.stringify(prevValues.territorialLegalBasis) !== JSON.stringify(curValues.territorialLegalBasis),
                children: ({ getFieldValue })=>{
                    const territorialLegalBasis = getFieldValue("territorialLegalBasis");
                    const { legalBasis, labelGdpr, labelGdprEuEea, safeCountriesList, adequacyCountriesLists, dataProcessingInUnsafeCountriesArticles, dataProcessingInUnsafeCountriesArticlesLinks } = calculateTerritorialLegalBasisArticles(territorialLegalBasis);
                    return /*#__PURE__*/ _jsxs(Form.Item, {
                        label: __("Consent for data processing in unsafe third countries"),
                        children: [
                            /*#__PURE__*/ _jsx(Form.Item, {
                                name: "isDataProcessingInUnsafeCountries",
                                noStyle: true,
                                children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Radio.Button, {
                                            disabled: !isPro,
                                            value: true,
                                            children: __("Enabled")
                                        }),
                                        /*#__PURE__*/ _jsx(Radio.Button, {
                                            disabled: !isPro,
                                            value: false,
                                            children: __("Disabled")
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                style: {
                                    margin: "5px 0px 0px"
                                },
                                children: /*#__PURE__*/ _jsx(UpsellTag, {
                                    title: __("Do you need consent for data processing in unsafe countries?"),
                                    testDrive: true,
                                    feature: "data-processing-in-unsafe-countries",
                                    assetName: __("pro-modal/data-processing-in-insecure-third-countries.png"),
                                    description: _i(__("The GDPR offers a great level of data protection. For data processing in countries where the GDPR does not apply, safety mechanisms are required that guarantee an appropriate level of protection. This is usually an adequacy decision or standard contractual clauses. If none of the safety mechanisms are in place, you must inform your website visitors about special risks and their consequences and obtain special consent in accordance with {{aGdpr}}Art. 49 GDPR{{/aGdpr}}."), {
                                        aGdpr: /*#__PURE__*/ _jsx("a", {
                                            href: __("https://gdpr-text.com/read/article-49/"),
                                            target: "_blank",
                                            rel: "noreferrer"
                                        })
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: "description",
                                children: [
                                    _i(__(`Countries in which {{legalBasis/}} are not applicable are considered as unsafe third countries in which no personal data of your visitors may initially be transmitted or processed. For some countries, there is an {{adequacy}}{{u}}adequacy decision{{/u}}{{/adequacy}} that declares the country to be safe. You can also conclude {{aSCC}}standard contractual clauses{{/aSCC}} with service providers that contractually guarantee safe data processing. Sometimes, however, none of these safety mechanisms exist and it is not possible to waive the use of the service. In accordance with {{article/}}, it is possible to obtain the consent of website visitors for the one-off and multiple processing of personal data. Consent to the regular processing of data in unsafe third countries in this way is controversial. This means that at least some of the convenient services can still be used. Activate this option to flag the processing of personal data in unsafe countries and obtain the special consent from your visitors.`), {
                                        legalBasis: jsxJoin(legalBasis.map((s)=>{
                                            const tooltip = [
                                                labelGdpr,
                                                labelGdprEuEea
                                            ].indexOf(s) > -1 ? safeCountriesList["GDPR"] : "";
                                            return tooltip ? /*#__PURE__*/ _jsx(Tooltip, {
                                                title: tooltip,
                                                children: /*#__PURE__*/ _jsx("u", {
                                                    style: {
                                                        textDecorationStyle: "dashed"
                                                    },
                                                    children: s
                                                })
                                            }, s) : /*#__PURE__*/ _jsx("span", {
                                                children: s
                                            }, s);
                                        }), __(" or ")),
                                        article: jsxJoin(dataProcessingInUnsafeCountriesArticles.map((article, i)=>{
                                            return /*#__PURE__*/ _jsx("a", {
                                                href: dataProcessingInUnsafeCountriesArticlesLinks[i],
                                                target: "_blank",
                                                rel: "noreferrer",
                                                children: article
                                            }, article);
                                        }), __(" or ")),
                                        u: /*#__PURE__*/ _jsx("u", {
                                            style: {
                                                textDecorationStyle: "dashed"
                                            }
                                        }),
                                        adequacy: /*#__PURE__*/ _jsx(Tooltip, {
                                            title: jsxJoin(adequacyCountriesLists.map((s)=>/*#__PURE__*/ _jsx("div", {
                                                    children: s
                                                }, s)), /*#__PURE__*/ _jsx("br", {}))
                                        }),
                                        aSCC: /*#__PURE__*/ _jsx("a", {
                                            href: __("https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection/standard-contractual-clauses-scc_en"),
                                            target: "_blank",
                                            rel: "noreferrer"
                                        })
                                    }),
                                    "\xa0•\xa0",
                                    /*#__PURE__*/ _jsx("a", {
                                        onClick: ()=>onCustomizeClick("rcb-banner-texts-data-processing-in-unsafe-countries"),
                                        children: __("Change text in customizer")
                                    })
                                ]
                            })
                        ]
                    });
                }
            }),
            /*#__PURE__*/ _jsxs(Form.Item, {
                label: __("Age notice for consent"),
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "isAgeNotice",
                        noStyle: true,
                        children: /*#__PURE__*/ _jsxs(Radio.Group, {
                            children: [
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    value: true,
                                    children: __("Enabled")
                                }),
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    value: false,
                                    children: __("Disabled")
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "description",
                        children: [
                            _i(__(`In accordance with {{a}}Art. 8 GDPR{{/a}}, consent to services that process personal data and/or set cookies can only be given from the age of 16 years (varying in some EU countries) or together with a legal guardian. If your website does not clearly and exclusively address adults (e.g. dating or porn sites), you as the website operator must inform children under the minimum age in simple language that they are not allowed to consent to non-essential services without their legal guardian.`), {
                                a: /*#__PURE__*/ _jsx("a", {
                                    href: __("https://gdpr-text.com/read/article-8/"),
                                    target: "_blank",
                                    rel: "noreferrer"
                                })
                            }),
                            "\xa0•\xa0",
                            /*#__PURE__*/ _jsx("a", {
                                onClick: ()=>onCustomizeClick("rcb-banner-texts-age-notice"),
                                children: __("Change text in customizer")
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        shouldUpdate: ({ isAgeNotice: isAgeNoticeA, operatorCountry: operatorCountryA, ageNoticeAgeLimit: ageNoticeAgeLimitA }, { isAgeNotice: isAgeNoticeB, operatorCountry: operatorCountryB, ageNoticeAgeLimit: ageNoticeAgeLimitB })=>isAgeNoticeA !== isAgeNoticeB || operatorCountryA !== operatorCountryB || ageNoticeAgeLimitA !== ageNoticeAgeLimitB,
                        children: ({ getFieldValue })=>/*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsxs(Space.Compact, {
                                        className: "rcb-antd-select-addon",
                                        style: {
                                            display: getFieldValue("isAgeNotice") && showAgeNoticeAgeLimitDropdown ? undefined : "none"
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(Input, {
                                                addonBefore: __("Age limit")
                                            }),
                                            /*#__PURE__*/ _jsx(Form.Item, {
                                                noStyle: true,
                                                name: "ageNoticeAgeLimit",
                                                children: /*#__PURE__*/ _jsx(Select, {
                                                    showSearch: true,
                                                    optionFilterProp: "children",
                                                    children: Object.keys(ageNoticeCountryAgeMap).map((key)=>{
                                                        const operatorCountry = getFieldValue("operatorCountry");
                                                        const useAgeKey = key === "INHERIT" ? operatorCountry && ageNoticeCountryAgeMap[operatorCountry] ? operatorCountry : "GDPR" : key;
                                                        return /*#__PURE__*/ _jsx(Select.Option, {
                                                            value: key,
                                                            children: /*#__PURE__*/ _jsxs(Row, {
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Col, {
                                                                        flex: "auto",
                                                                        children: key === "INHERIT" ? __("Determine age limit based on specified website operator country") : key === "GDPR" ? __("GDPR standard") : iso3166OneAlpha2[key]
                                                                    }),
                                                                    /*#__PURE__*/ _jsx(Col, {
                                                                        flex: "none",
                                                                        children: __("%d years", ageNoticeCountryAgeMap[useAgeKey])
                                                                    })
                                                                ]
                                                            })
                                                        }, key);
                                                    })
                                                })
                                            })
                                        ]
                                    }),
                                    getFieldValue("isAgeNotice") && showAgeNoticeAgeLimitDropdown && [
                                        "INHERIT",
                                        "GDPR"
                                    ].indexOf(getFieldValue("ageNoticeAgeLimit")) === -1 && /*#__PURE__*/ _jsx("div", {
                                        className: "notice notice-info inline below-h2 notice-alt",
                                        style: {
                                            margin: "10px 0 0"
                                        },
                                        children: /*#__PURE__*/ _jsx("p", {
                                            children: _i(__("The age limit should only differ from the age limit in the country of the website operator if you are primarily targeting visitors from another country with a {{strong}}higher{{/strong}} age limit. Changing the age limit therefore only makes sense in a few exceptional cases."), {
                                                strong: /*#__PURE__*/ _jsx("strong", {})
                                            })
                                        })
                                    })
                                ]
                            })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Form.Item, {
                label: __("Naming of all services in first view"),
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "isListServicesNotice",
                        noStyle: true,
                        children: /*#__PURE__*/ _jsxs(Radio.Group, {
                            children: [
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    value: true,
                                    children: __("Enabled")
                                }),
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    value: false,
                                    children: __("Disabled")
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "description",
                        children: [
                            __("The visitor to your website should be adequately informed of what he or she is agreeing to when consenting to all services. Therefore, in the first view of the cookie banner, all services can be written out by name in a comma-separated list. Your website visitor can thus better assess whether he really wants to agree to all services without visiting the individual privacy settings."),
                            "\xa0•\xa0",
                            /*#__PURE__*/ _jsx("a", {
                                onClick: ()=>onCustomizeClick("rcb-banner-texts-list-services-notice"),
                                children: __("Change text in customizer")
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: (prevValues, nextValues)=>prevValues.isBannerLessConsent !== nextValues.isBannerLessConsent || prevValues.cookiePolicyId !== nextValues.cookiePolicyId || prevValues.isTcf !== nextValues.isTcf,
                children: ({ getFieldValue, setFieldsValue })=>{
                    const isBannerLessConsent = getFieldValue("isBannerLessConsent");
                    const cookiePolicyId = getFieldValue("cookiePolicyId");
                    const isTcf = getFieldValue("isTcf");
                    return /*#__PURE__*/ _jsxs(Form.Item, {
                        label: __("Banner-less consent"),
                        children: [
                            /*#__PURE__*/ _jsx(Form.Item, {
                                name: "isBannerLessConsent",
                                noStyle: true,
                                children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                    style: {
                                        display: isBannerLessConsent ? undefined : "none"
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Radio.Button, {
                                            disabled: !isPro,
                                            value: true,
                                            children: __("Enabled")
                                        }),
                                        /*#__PURE__*/ _jsx(Radio.Button, {
                                            disabled: !isPro,
                                            value: false,
                                            children: __("Disabled")
                                        })
                                    ]
                                })
                            }),
                            !isBannerLessConsent && /*#__PURE__*/ _jsxs(Radio.Group, {
                                value: false,
                                children: [
                                    /*#__PURE__*/ _jsx(Popconfirm, {
                                        title: /*#__PURE__*/ _jsxs(Space, {
                                            direction: "vertical",
                                            size: "small",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    children: __("Enabling the option means that the cookie banner will no longer be displayed on your website. Consent to certain services can only be given via visual content blockers or the legal link to the privacy settings (only a few website visitors use this). To comply with your information obligations, especially for essential services, you must have a cookie policy on your website or provide all the necessary information manually in your privacy policy. The document must be linked on every subpage of the website.")
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    children: cookiePolicyId > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(CheckCircleFilled, {
                                                                style: {
                                                                    color: "#52c41a"
                                                                }
                                                            }),
                                                            "\xa0",
                                                            __("Cookie policy page already created")
                                                        ]
                                                    }) : /*#__PURE__*/ _jsx(Checkbox, {
                                                        defaultChecked: true,
                                                        children: __("Create cookie policy page with content automatically")
                                                    })
                                                }),
                                                servicesWithoutVisualContentBlocker.length > 0 && /*#__PURE__*/ _jsx(Alert, {
                                                    type: "warning",
                                                    message: _n("The service %s does not have a visual content blocker, which is why website visitors without cookie banners cannot consent to it (except via the legal link to change the privacy settings). As a result, this service will normally no longer be embeded on your website.", "The services %s do not have visual content blockers, which is why website visitors without cookie banners cannot consent to them (except via the legal link to change the privacy settings). As a result, these services will normally no longer be embeded on your website.", servicesWithoutVisualContentBlocker.length, joinWithAndSeparator(servicesWithoutVisualContentBlocker, __(" and "))),
                                                    showIcon: true
                                                }),
                                                isTcf && /*#__PURE__*/ _jsx(Alert, {
                                                    type: "warning",
                                                    message: __("Banner-less consent will preventing visitors from consenting to TCF vendor purposes, which will result in significantly reduced advertising revenue. We advise against using the Transparency & Consent Framework together with banner-less consent!"),
                                                    showIcon: true
                                                })
                                            ]
                                        }, "alerts"),
                                        cancelText: __("Cancel"),
                                        okText: __("Activate now!"),
                                        overlayStyle: {
                                            maxWidth: 750
                                        },
                                        onCancel: ()=>{
                                            setFieldsValue({
                                                isBannerLessConsent: false
                                            });
                                        },
                                        onConfirm: async (e)=>{
                                            setFieldsValue({
                                                isBannerLessConsent: true
                                            });
                                            const popconfirm = parents(e.target, ".rcb-antd-popconfirm", 1);
                                            // Automatically create the cookie policy page
                                            if (popconfirm[0].querySelector("input").checked) {
                                                await handleCreateCookiePolicy();
                                            }
                                        },
                                        placement: "bottomLeft",
                                        children: /*#__PURE__*/ _jsx(Radio.Button, {
                                            disabled: !isPro,
                                            value: true,
                                            onClick: (e)=>e.stopPropagation(),
                                            onChange: (e)=>e.stopPropagation(),
                                            children: __("Enabled")
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                        disabled: !isPro,
                                        value: false,
                                        children: __("Disabled")
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                style: {
                                    margin: "5px 0px 0px",
                                    display: isPro ? "none" : undefined
                                },
                                children: /*#__PURE__*/ _jsx(UpsellTag, {
                                    title: __("Do you want to avoid using a cookie banner?"),
                                    testDrive: true,
                                    feature: "banner-less-consent",
                                    assetName: __("pro-modal/banner-less-consent.png"),
                                    description: __("Bannerless consent ensures a seamless, distraction-free experience for your visitors. By using essential services and visual content blockers, you can avoid intrusive cookie banners and at the same time allow users to easily manage their consent for specific content such as YouTube videos in accordance with the legal regulations.")
                                })
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "description",
                                style: {
                                    marginTop: 5
                                },
                                children: __("If you only use essential services and those with a visual content blocker, you can avoid using a cookie banner so that your website visitors are not distracted. Visual content blockers initially block all services that require consent. For example, if a visitor wants to watch a YouTube video, they can give their consent in the content blocker for these videos to always be loaded in future. To fulfill your information obligations, you should have a cookie policy on your website (created automatically) or provide all necessary information in your privacy policy (manually).")
                            })
                        ]
                    });
                }
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: (prevValues, nextValues)=>prevValues.isBannerLessConsent !== nextValues.isBannerLessConsent,
                children: ({ getFieldValue })=>{
                    const isBannerLessConsent = getFieldValue("isBannerLessConsent");
                    return isBannerLessConsent && isPro && /*#__PURE__*/ _jsxs(Form.Item, {
                        label: __("Show cookie banner on specific pages"),
                        children: [
                            /*#__PURE__*/ _jsx(Form.Item, {
                                name: "bannerLessConsentShowOnPageIds",
                                noStyle: true,
                                children: /*#__PURE__*/ _jsx(RenderPageSelector, {
                                    id: "bannerLessConsentShowOnPageIds",
                                    disabled: false,
                                    multiple: true,
                                    onlyPages: false
                                })
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "description",
                                children: __("The cookie banner is generally not displayed due to consent being obtained without a banner (banner-less). However, if non-essential services without visual content blockers are required on certain subpages, you can display the cookie banner when the visitor accesses one of these subpages for the first time. This is useful e.g. for using services like Google Ads Conversion Tracking or Google Analytics on specific landing pages.")
                            })
                        ]
                    });
                }
            }),
            /*#__PURE__*/ _jsxs(Form.Item, {
                label: __("Handling of failed consent documentation"),
                style: {
                    marginBottom: 0
                },
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "failedConsentDocumentationHandling",
                        noStyle: true,
                        children: /*#__PURE__*/ _jsxs(Radio.Group, {
                            children: [
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    value: "essentials",
                                    children: __("Allow only essential services")
                                }),
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    value: "optimistic",
                                    children: __("Respect user consent")
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "description",
                        children: __("It is possible that your website can still be accessed (e.g. due to a highly scalable page cache), but that consents given in the cookie banner can no longer be documented (e.g. server overload due to too much traffic). In this case, Real Cookie Banner will try to document the consent as soon as the server is avaible again in accordance with your legal obligations. In the meantime, there is a risk if the consent of your website visitor is respected and thus non-essential services are played out whose consent or legitimate interest you cannot prove. However, if you expect a temporary overload of your server, e.g. due to an event, it may be economically advantageous to take this risk.")
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Collapse, {
                ghost: true,
                children: /*#__PURE__*/ _jsx(Panel, {
                    header: /*#__PURE__*/ _jsx("a", {
                        children: __("Advanced settings (Do not track, behavior for bots, saving of IP addresses, duration of cookie consent, lifetime of consent documentations)")
                    }),
                    children: /*#__PURE__*/ _jsxs(Card, {
                        style: {
                            margin: 5
                        },
                        children: [
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __('Respect "Do Not Track"'),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "isRespectDoNotTrack",
                                        noStyle: true,
                                        children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Radio.Button, {
                                                    value: true,
                                                    children: __("Enabled")
                                                }),
                                                /*#__PURE__*/ _jsx(Radio.Button, {
                                                    value: false,
                                                    children: __("Disabled")
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        style: {
                                            margin: "5px 0px 0px"
                                        },
                                        children: /*#__PURE__*/ _jsx(LearnMoreTag, {
                                            url: __("https://devowl.io/data-protection/do-not-track/")
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "description",
                                        children: [
                                            __(`Users can set the "Do not track" HTTP header in their browser to indicate that they do not want to be tracked. If you enable this option, users will be able to access your website with this HTTP header without a cookie banner, if there are no non-essential services that are used based on legitimate interest. Only the non-rejectable services will be used in the case.`),
                                            "\xa0",
                                            isTcf && /*#__PURE__*/ _jsx("strong", {
                                                children: __("According to the TCF standard, all rights of objection are also exercised for these visitors, as the cookie banner is not displayed at all and thus no transparency about legitimate interest can be established.")
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("Automatically accept all services for bots"),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "isAcceptAllForBots",
                                        noStyle: true,
                                        children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Radio.Button, {
                                                    value: true,
                                                    children: __("Enabled")
                                                }),
                                                /*#__PURE__*/ _jsx(Radio.Button, {
                                                    value: false,
                                                    children: __("Disabled")
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        children: _i(__("Bots are not subject of the GDPR according to {{aGdpr}}Art. 1 GDPR{{/aGdpr}} and technically cannot give consent to the processing of personal data and the setting of cookies. As a result, they may consider your site as slow if a cookie banner permanently blocks all other content. Therefore, you should allow the bots to browse your site without consent, but still allow all services."), {
                                            aGdpr: /*#__PURE__*/ _jsx("a", {
                                                href: __("https://gdpr-text.com/read/article-1/"),
                                                target: "_blank",
                                                rel: "noreferrer"
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("Save IP address on consent"),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "isSaveIp",
                                        noStyle: true,
                                        children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Radio.Button, {
                                                    value: true,
                                                    children: __("Enabled")
                                                }),
                                                /*#__PURE__*/ _jsx(Radio.Button, {
                                                    value: false,
                                                    children: __("Disabled")
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        children: _i(__(`Depending on the country of origin, it may make legal sense to store the IP address of the user who consents. On the other hand, you must inform the user in your privacy policy that you are storing this IP address together with the consent, as this is legally {{a}}personal data{{/a}}.`), {
                                            a: /*#__PURE__*/ _jsx("a", {
                                                href: __("https://devowl.io/data-protection/personal-data-gdpr/"),
                                                target: "_blank",
                                                rel: "noreferrer"
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("Duration of cookie consent"),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "cookieDuration",
                                        wrapperCol: {
                                            span: 8
                                        },
                                        style: {
                                            marginBottom: 0
                                        },
                                        rules: [
                                            {
                                                type: "number",
                                                min: 1,
                                                max: 365,
                                                transform: (v)=>+v,
                                                message: __("Please use a number between %d and %d!", 1, 365)
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            addonAfter: __("days"),
                                            type: "number",
                                            max: 365
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        children: __(`The consent of a user must be limited in time. You can define how long the consent is valid. It can be valid for a maximum of 365 days because every 6-12 months you have to remind the user of the right to revoke consent (cookie banner is displayed again). If you change the behavior or content of the cookie banner meanwhile, visitors to your website will have to give their consent again, even if the time period has not yet expired.`)
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        noStyle: true,
                                        shouldUpdate: (prevValues, nextValues)=>prevValues.cookieDuration !== nextValues.cookieDuration,
                                        children: ({ getFieldValue })=>getFieldValue("cookieDuration") < 183 && /*#__PURE__*/ _jsx("div", {
                                                className: "notice notice-warning inline below-h2 notice-alt",
                                                style: {
                                                    margin: 0
                                                },
                                                children: /*#__PURE__*/ _jsx("p", {
                                                    children: __("You are not allowed to ask for consent again in some EU countries, such as Italy, for at least six months (unless the content of the cookie banner changes). In other countries, you must ask for consent again after 12 months at the latest. Therefore, consent should be valid for between 183 days and 365 days.")
                                                })
                                            })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("Delete documented consents after"),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "consentDuration",
                                        wrapperCol: {
                                            span: 8
                                        },
                                        style: {
                                            marginBottom: 0
                                        },
                                        rules: [
                                            {
                                                type: "number",
                                                min: 1,
                                                max: 120,
                                                transform: (v)=>+v,
                                                message: __("Please use a number between %d and %d!", 1, 120)
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            addonAfter: __("months"),
                                            type: "number"
                                        })
                                    }),
                                    consentsDeletedAt && /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        children: /*#__PURE__*/ _jsx("strong", {
                                            children: __("Consents was last cleaned up on %s.", new Date(consentsDeletedAt).toLocaleString(document.documentElement.lang))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        children: _i(__(`Consents are automatically documented in order to be able to prove compliance with the legal requirements according to {{a}}Art. 5 GDPR{{/a}} and, in case of dispute, to prove how the consent was obtained. The absolute statute of limitations for data protection violations in most EU countries is 10 years. After that, you should delete consents in the interest of data economy.`), {
                                            a: /*#__PURE__*/ _jsx("a", {
                                                href: __("https://gdpr-text.com/read/article-5/"),
                                                target: "_blank",
                                                rel: "noreferrer"
                                            })
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                }, "nav")
            })
        ]
    });
};
export { FormSettingsConsentTabContent };
